/**
 * errorBoundary.js — одно место, куда стекаются исключения.
 *
 * [10.0.3] Взято из присланного файла. Что изменено и почему.
 *
 * 1. Убрана отправка на /api/errors по умолчанию.
 *    В присланном виде каждая необработанная ошибка уходила POST-запросом на
 *    /api/errors и несла с собой стек, адрес страницы и строку браузера. Два
 *    возражения, и оба серьёзные. Первое: сервера с таким адресом нет —
 *    программа лежит на статике, значит на каждую ошибку прилетал бы 404, а
 *    при обрыве связи ещё и висящий запрос. Второе, важнее: адрес страницы у
 *    медицинской программы может содержать идентификатор исследования, а стек —
 *    имена файлов пациента. Отправлять это наружу без ведома врача нельзя.
 *    Теперь отправка выключена и включается явно: setErrorEndpoint('...').
 *
 * 2. Счётчик ошибок сбрасывается по времени, а не навсегда.
 *    Было: сотня ошибок — и модуль замолкает до перезагрузки страницы. Один
 *    неудачный кадр в цикле отрисовки съедал лимит за секунду, и всё
 *    последующее, включая настоящую поломку, терялось. Теперь лимит
 *    посекундный: защита от лавины остаётся, но окно наблюдения не закрывается.
 *
 * 3. Одинаковые ошибки схлопываются.
 *    Ошибка внутри requestAnimationFrame повторяется шестьдесят раз в секунду.
 *    Считаем повторы и пишем в журнал одну строку с их числом.
 */

import { log } from './logger.js';

const IS_DEV = typeof window !== 'undefined'
  && (window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
    || window.location.search.includes('debug=true'));

/** Сколько ошибок в секунду ещё разбираем, дальше — только считаем. */
const MAX_PER_SECOND = 20;

/** Куда слать отчёты. Пусто — никуда, и это положение по умолчанию. */
let _endpoint = '';

/**
 * Включить отправку отчётов об ошибках на свой адрес.
 * Пока не вызвано — ничего никуда не уходит.
 * @param {string} url
 */
export function setErrorEndpoint(url) {
  _endpoint = typeof url === 'string' ? url : '';
}

export class ErrorBoundary {
  static #windowStart = 0;
  static #inWindow = 0;
  static #suppressed = 0;
  static #lastKey = '';
  static #lastKeyCount = 0;

  /**
   * @param {Error|any} error
   * @param {string} context где произошло
   * @param {object} [extra] что ещё известно
   */
  static capture(error, context, extra = {}) {
    const now = Date.now();
    if (now - this.#windowStart > 1000) {
      if (this.#suppressed) {
        log.warn(`[ErrorBoundary] заглушено однотипных ошибок: ${this.#suppressed}`);
        this.#suppressed = 0;
      }
      this.#windowStart = now;
      this.#inWindow = 0;
    }
    // Считаем разобранные и заглушённые раздельно: так в состоянии видно
    // «двадцать разобрали, сто восемьдесят придержали», а не одно число.
    if (this.#inWindow >= MAX_PER_SECOND) { this.#suppressed++; return; }
    this.#inWindow++;

    const err = (error instanceof Error) ? error : new Error(String(error?.message || error));
    const key = `${context}|${err.message}`;

    // Одна и та же ошибка подряд — не разводим журнал на шестьдесят строк в секунду.
    if (key === this.#lastKey) {
      this.#lastKeyCount++;
      if (this.#lastKeyCount % 50 !== 0) return;
      log.error(`[${context}] ${err.message} · повторилось ${this.#lastKeyCount} раз`);
    } else {
      this.#lastKey = key;
      this.#lastKeyCount = 1;
      log.error(`[${context}]`, err, extra);
    }

    if (IS_DEV) this.#showDevToast(err, context);
    else this.#report(err, context, extra);
  }

  static #showDevToast(error, context) {
    const message = `Ошибка в ${context}: ${error.message}`;
    try {
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(message, 'error', 8000);
        return;
      }
    } catch (e) { /* показать не удалось — уже записано в журнал */ }
    console.error(message);
  }

  static #report(error, context, extra) {
    try {
      if (typeof window !== 'undefined' && window.Sentry) {
        window.Sentry.captureException(error, { tags: { context }, extra });
        return;
      }
      if (typeof window !== 'undefined' && window.bugsnagClient) {
        window.bugsnagClient.notify(error, { metaData: { context, ...extra } });
        return;
      }
      if (_endpoint) this.#send(error, context, extra);
    } catch (e) { /* отчёт об ошибке не должен ломать программу */ }
  }

  static async #send(error, context, extra) {
    try {
      await fetch(_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          message: error.message,
          stack: error.stack,
          context,
          extra,
          timestamp: Date.now(),
          // Адрес страницы НЕ отправляем: у медицинской программы в нём может
          // оказаться идентификатор исследования.
          userAgent: navigator.userAgent,
        }),
      });
    } catch (e) { /* молча: отчётность не важнее работы */ }
  }

  /** Состояние — для панели диагностики. */
  static state() {
    return { inWindow: this.#inWindow, suppressed: this.#suppressed, endpoint: _endpoint || null };
  }

  static reset() {
    this.#windowStart = 0; this.#inWindow = 0;
    this.#suppressed = 0; this.#lastKey = ''; this.#lastKeyCount = 0;
  }
}

let _globalAC = null;

/** Поставить глобальные ловушки. Повторный вызов переустанавливает, а не множит. */
export function setupGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  if (_globalAC) _globalAC.abort();
  _globalAC = new AbortController();
  const opts = { signal: _globalAC.signal };

  window.addEventListener('error', (event) => {
    // Сбой загрузки картинки или скрипта приходит сюда же, но без event.error.
    const t = event.target;
    if (t && (t.tagName === 'SCRIPT' || t.tagName === 'LINK' || t.tagName === 'IMG')) {
      ErrorBoundary.capture(
        new Error(`не загрузился ${t.src || t.href || t.tagName}`),
        'Загрузка ресурса',
      );
      return;
    }
    ErrorBoundary.capture(event.error || event.message, 'Глобальная ошибка', {
      filename: event.filename, lineno: event.lineno, colno: event.colno,
    });
  }, { ...opts, capture: true });

  window.addEventListener('unhandledrejection', (event) => {
    ErrorBoundary.capture(event.reason, 'Необработанный промис');
    // preventDefault НЕ вызываем: в консоли браузера такие ошибки должны
    // оставаться видимыми — иначе отладка вслепую.
  }, opts);
}

export function teardownGlobalErrorHandlers() {
  if (_globalAC) { _globalAC.abort(); _globalAC = null; }
}

export default { ErrorBoundary, setupGlobalErrorHandlers, teardownGlobalErrorHandlers, setErrorEndpoint };
